import React, { useState } from 'react';
import { useData } from '../DataContext';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Trash2, Mail, Reply, Eye, Loader2 } from 'lucide-react';
import useScrambleText from '../../hooks/useScrambleText';

const AdminMessages = () => {
  const { messages, updateMessageStatus, deleteMessage, loading } = useData();
  const h1Ref = useScrambleText("Inbox");
  const [filter, setFilter] = useState('all');
  const [processingId, setProcessingId] = useState(null);

  const filteredMessages = filter === 'all' 
    ? messages 
    : messages.filter(m => m.status === filter);

  const getStatusBadge = (status) => {
    const badges = {
      unread: { color: 'bg-accent/20 text-accent', label: 'Unread' },
      read: { color: 'bg-blue-500/20 text-blue-400', label: 'Read' },
      replied: { color: 'bg-green-500/20 text-green-400', label: 'Replied' }
    };
    const badge = badges[status] || badges.unread;
    return (
      <span className={`px-2 py-1 rounded text-xs ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const handleStatusUpdate = async (id, status) => {
    setProcessingId(id);
    try {
      await updateMessageStatus(id, status);
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      setProcessingId(id);
      try {
        await deleteMessage(id);
      } catch (error) {
        console.error('Failed to delete message:', error);
      } finally {
        setProcessingId(null);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 ref={h1Ref} className="text-3xl font-heading font-bold korov-gradient-text mb-2 cursor-pointer">Inbox</h1>
          <p className="text-muted-foreground">Messages from the void ({messages.length})</p>
        </div>
        
        {/* Filter Buttons */}
        <div className="flex gap-2">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
            className={filter === 'all' ? 'bg-primary text-black' : ''}
            data-testid="filter-all-btn"
          >
            All ({messages.length})
          </Button>
          <Button
            variant={filter === 'unread' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('unread')}
            className={filter === 'unread' ? 'bg-accent text-white' : ''}
            data-testid="filter-unread-btn"
          >
            Unread ({messages.filter(m => m.status === 'unread').length})
          </Button>
          <Button
            variant={filter === 'read' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('read')}
            className={filter === 'read' ? 'bg-blue-500 text-white' : ''}
            data-testid="filter-read-btn"
          >
            Read ({messages.filter(m => m.status === 'read').length})
          </Button>
          <Button
            variant={filter === 'replied' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('replied')}
            className={filter === 'replied' ? 'bg-green-500 text-white' : ''}
            data-testid="filter-replied-btn"
          >
            Replied ({messages.filter(m => m.status === 'replied').length})
          </Button>
        </div>
      </div>

      <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
        <Table>
          <TableHeader className="bg-white/5">
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-primary">Date</TableHead>
              <TableHead className="text-primary">Sender</TableHead>
              <TableHead className="text-primary">Message</TableHead>
              <TableHead className="text-primary">Status</TableHead>
              <TableHead className="text-right text-primary">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredMessages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                  {filter === 'all' ? 'No messages received yet.' : `No ${filter} messages.`}
                </TableCell>
              </TableRow>
            ) : (
              filteredMessages.map((msg) => (
                <TableRow 
                  key={msg.id} 
                  className={`border-white/10 hover:bg-white/5 ${msg.status === 'unread' ? 'bg-accent/5' : ''}`}
                  data-testid={`message-row-${msg.id}`}
                >
                  <TableCell className="text-muted-foreground whitespace-nowrap">{msg.date}</TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <Mail className={`w-4 h-4 ${msg.status === 'unread' ? 'text-accent' : 'text-primary'}`} />
                        <span className="font-medium text-white">{msg.name}</span>
                      </div>
                      <span className="text-xs text-muted-foreground ml-6">{msg.email}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-white/80 max-w-md truncate">{msg.message}</TableCell>
                  <TableCell>{getStatusBadge(msg.status)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {processingId === msg.id ? (
                        <Loader2 className="w-4 h-4 animate-spin text-primary" />
                      ) : (
                        <>
                          {msg.status === 'unread' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleStatusUpdate(msg.id, 'read')}
                              className="hover:bg-blue-500/10 hover:text-blue-400"
                              title="Mark as Read"
                              data-testid={`mark-read-${msg.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                          {msg.status !== 'replied' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                handleStatusUpdate(msg.id, 'replied');
                                window.location.href = `mailto:${msg.email}`;
                              }}
                              className="hover:bg-green-500/10 hover:text-green-400"
                              title="Reply"
                              data-testid={`reply-${msg.id}`}
                            >
                              <Reply className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(msg.id)}
                            className="hover:bg-destructive/10 hover:text-destructive"
                            title="Delete"
                            data-testid={`delete-message-${msg.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AdminMessages;
